import { useEffect, useRef, useState } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { Autocomplete, TextField } from "@mui/material";

import { IOption } from "../../../../../../../../../shared/contracts/IReportDataOptions";
import IPivotChildColumn from "../../../../../../../../../shared/contracts/IPivotChildColumn";
import useWizardReportDataContext from "../../../../../../contexts/useWizardReportData.context";
import useWizardReportPivotService from "../../wizard-report-pivot.service";


/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Jan/05/2025
 */
export default function ChildGridGroupByFunctionsRenderer(
    props: ICellRendererParams<IPivotChildColumn>
) {

    const { availableColumns, pivotData, groupByOptions } = useWizardReportDataContext();
    const { getGroupByOptionsByDataType } = useWizardReportPivotService();

    const [options, setOptions] = useState<IOption[]>([]);
    const [selectedItem, setSelectedItem] = useState<IOption | null>(null);
    const [disabled, setDisabled] = useState(true);


    useEffect(() => {
        const isInvalid = !props.data?.childColumnName;

        // Enable/Disable
        setDisabled(isInvalid);

        if (isInvalid) {
            setSelectedItem(null); // clear existing value
        } else { // isValid
            const selectedColumn = availableColumns.find((item) =>
                item.columnName === props.data?.childColumnName
            );
            if (selectedColumn) {
                const options = getGroupByOptionsByDataType(selectedColumn.columnDataType);

                if (selectedItem && !options.find(x => x.optionValue === selectedItem?.optionValue)) {
                    setSelectedItem(null);
                }

                setOptions(options);
            }
        }

    }, [props.data?.childColumnName, selectedItem]);

    useEffect(() => {
        const data = props.node.data;

        if (groupByOptions && data && data.childColumnGroupByValue
            && data.childColumnGroupByValue !== selectedItem?.optionValue
        ) {
            const option = groupByOptions.options.find(x => x.optionValue === data.childColumnGroupByValue);
            if (option) {
                setSelectedItem(option)
            } else {
                setSelectedItem(null)
            }
        }

        if (!data?.childColumnGroupByValue && selectedItem !== null) {
            setSelectedItem(null)
        }

    }, [props, groupByOptions, pivotData?.parentColumn, pivotData?.childColumns])


    const handleSelectionChange = (selEvent: any, selOption: IOption | null) => {

        setSelectedItem(selOption);

        // If necessary, update the data object in AG Grid
        if (props.node.data) {
            props.node.setDataValue('childColumnGroupByValue', selOption?.optionValue ?? null);
            props.api.refreshCells({
                rowNodes: [props.node],
                columns: ['childColumnGroupByValue']
            });
        }

        // Manually trigger onRowDataUpdated
        props.api.dispatchEvent({ type: 'rowDataUpdated' });
    };



    return (<Autocomplete fullWidth size='small'
        disabled={disabled}
        options={options}
        value={selectedItem}
        onChange={handleSelectionChange}

        getOptionLabel={(option) => option?.optionName ?? ''}

        renderInput={(params) =>
            <TextField {...params}
                disabled={disabled}
                placeholder='select function...'
                sx={{
                    '& .MuiOutlinedInput-root': {
                        fontFamily: 'inherit', // Inherit font family from AG Grid
                        fontSize: 'inherit',   // Inherit font size from AG Grid
                        color: 'inherit',      // Inherit text color
                        '& fieldset': {
                            border: 'none',    // Remove border
                        },
                    },
                    '& .MuiInputBase-input': {
                        fontFamily: 'inherit', // Inherit font family
                        fontSize: 'inherit',   // Inherit font size
                        color: 'inherit',      // Inherit text color
                    },
                }}
            />}

        renderGroup={(params) => (
            <li key={params.key}>
                <ul style={{ padding: 0 }}>{params.children}</ul>
            </li>
        )}
    />);
}